import { useQuery } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { getCatalog } from '@/api/adapter/onboarding';
import type { CatalogEntry } from '@/types/session';
import type { Role } from '@/api/virto';
export type { Role };
export type Skill = CatalogEntry;
const catalogKey = ['onboarding', 'catalog'] as const;

export const qualificationKeys = {
  roles: ['roles'] as const,
  skills: ['skills'] as const,
};

/**
 * Unified hook to fetch, cache, and resolve roles and skills dynamically from backend catalogs.
 *
 * - Roles, skills and execution compensation policy share GET /api/catalog.
 * - Provides lookup maps and helper methods for converting between names and IDs.
 */
export function useSkillsAndRoles() {
  const rolesQuery = useQuery({
    queryKey: catalogKey, queryFn: getCatalog, staleTime: 5 * 60 * 1000, retry: false,
  });
  const skillsQuery = useQuery({
    queryKey: catalogKey, queryFn: getCatalog, staleTime: 5 * 60 * 1000, retry: false,
    select: (catalog) => catalog.skills,
  });

  const roles = useMemo<Role[]>(
    () => rolesQuery.data?.roles.map((role) => ({ ...role, selectable: role.id !== 1 })) ?? [],
    [rolesQuery.data]
  );
  const selectableRoles = useMemo(
    () => roles.filter((r) => r.selectable),
    [roles]
  );
  const skills = useMemo(() => skillsQuery.data || [], [skillsQuery.data]);

  // Index maps for rapid lookup
  const roleById = useMemo(
    () => new Map<number, Role>(roles.map((r) => [r.id, r])),
    [roles]
  );
  const roleByName = useMemo(
    () => new Map<string, Role>(roles.map((r) => [r.name.toLowerCase().trim(), r])),
    [roles]
  );

  const skillById = useMemo(
    () => new Map<number, Skill>(skills.map((s) => [s.id, s])),
    [skills]
  );
  const skillByName = useMemo(
    () => new Map<string, Skill>(skills.map((s) => [s.name.toLowerCase().trim(), s])),
    [skills]
  );

  const getRoleName = useCallback(
    (roleId: number | undefined | null): string | undefined => {
      if (roleId === undefined || roleId === null) return undefined;
      return roleById.get(roleId)?.name;
    },
    [roleById]
  );

  const getRoleId = useCallback(
    (roleName: string | undefined | null): number | undefined => {
      if (!roleName) return undefined;
      const normalized = roleName.toLowerCase().trim();
      const direct = roleByName.get(normalized)?.id;
      if (direct !== undefined) return direct;

      // Handle hyphens or variations (e.g. "full-stack" vs "fullstack")
      const slug = normalized.replace(/[^a-z0-9]+/g, '');
      for (const [rName, role] of roleByName.entries()) {
        if (rName.replace(/[^a-z0-9]+/g, '') === slug) {
          return role.id;
        }
      }

      return undefined;
    },
    [roleByName]
  );

  const getSkillName = useCallback(
    (skillId: number | undefined | null): string | undefined => {
      if (skillId === undefined || skillId === null) return undefined;
      return skillById.get(skillId)?.name;
    },
    [skillById]
  );

  const getSkillId = useCallback(
    (skillName: string | undefined | null): number | undefined => {
      if (!skillName) return undefined;
      const normalized = skillName.toLowerCase().trim();
      const direct = skillByName.get(normalized)?.id;
      if (direct !== undefined) return direct;

      const slug = normalized.replace(/[^a-z0-9]+/g, '');
      for (const [sName, skill] of skillByName.entries()) {
        if (sName.replace(/[^a-z0-9]+/g, '') === slug) {
          return skill.id;
        }
      }

      return undefined;
    },
    [skillByName]
  );

  /**
   * Resolves a mixed array of skill IDs or skill names into unique numeric skill IDs.
   */
  const resolveSkillIds = useCallback(
    (references: (string | number)[]): number[] => {
      const ids: number[] = [];
      for (const item of references) {
        if (typeof item === 'number') {
          if (skillById.has(item)) ids.push(item);
        } else if (typeof item === 'string' && item.trim()) {
          const numeric = Number(item.trim());
          if (Number.isInteger(numeric) && numeric > 0 && skillById.has(numeric)) {
            ids.push(numeric);
          } else {
            const foundId = getSkillId(item);
            if (foundId !== undefined) ids.push(foundId);
          }
        }
      }
      return [...new Set(ids)].sort((a, b) => a - b);
    },
    [skillById, getSkillId]
  );

  /**
   * Resolves a mixed array of skill IDs or skill names into normalized skill names.
   */
  const resolveSkillNames = useCallback(
    (references: (string | number)[]): string[] => {
      const names: string[] = [];
      for (const item of references) {
        if (typeof item === 'number') {
          const name = getSkillName(item);
          if (name) names.push(name);
        } else if (typeof item === 'string' && item.trim()) {
          const trimmed = item.trim();
          const numeric = Number(trimmed);
          if (Number.isInteger(numeric) && numeric > 0) {
            const name = getSkillName(numeric);
            if (name) {
              names.push(name);
              continue;
            }
          }
          names.push(trimmed);
        }
      }
      return [...new Set(names)];
    },
    [getSkillName]
  );

  /**
   * Checks whether a role ID or role name is selectable for qualifications/registration.
   * Excludes coordinator (id: 1) and any role with selectable === false.
   */
  const isRoleSelectable = useCallback(
    (roleIdOrName: number | string | undefined | null): boolean => {
      if (roleIdOrName === undefined || roleIdOrName === null) return false;
      if (typeof roleIdOrName === 'number') {
        if (roleIdOrName === 1) return false;
        const role = roleById.get(roleIdOrName);
        return role?.selectable ?? false;
      }
      const normalized = roleIdOrName.toLowerCase().trim();
      if (normalized === 'coordinator') return false;
      const role = roleByName.get(normalized);
      return role?.selectable ?? false;
    },
    [roleById, roleByName]
  );

  /**
   * Filters a list of role IDs, discarding non-selectable roles (like coordinator).
   */
  const filterSelectableRoleIds = useCallback(
    (references: (string | number)[]): number[] => {
      const ids: number[] = [];
      for (const item of references) {
        if (typeof item === 'number') {
          if (isRoleSelectable(item)) ids.push(item);
        } else if (typeof item === 'string' && item.trim()) {
          const numeric = Number(item.trim());
          if (Number.isInteger(numeric) && numeric > 0) {
            if (isRoleSelectable(numeric)) ids.push(numeric);
          } else {
            const resolvedId = getRoleId(item);
            if (resolvedId !== undefined && isRoleSelectable(resolvedId)) {
              ids.push(resolvedId);
            }
          }
        }
      }
      return [...new Set(ids)].sort((a, b) => a - b);
    },
    [isRoleSelectable, getRoleId]
  );

  return {
    compensationPolicy: rolesQuery.data?.compensationPolicy,
    roles,
    selectableRoles,
    skills,
    skillById,
    isLoading: rolesQuery.isLoading || skillsQuery.isLoading,
    isError: rolesQuery.isError || skillsQuery.isError,
    rolesQuery,
    skillsQuery,
    getRoleName,
    getRoleId,
    getSkillName,
    getSkillId,
    resolveSkillIds,
    resolveSkillNames,
    isRoleSelectable,
    filterSelectableRoleIds,
  };
}

/**
 * Hook to fetch skill IDs associated with specific role IDs dynamically from the backend.
 */
export function useRoleSkillIds(roleIds: (number | undefined)[]) {
  const validIds = useMemo(
    () =>
      [...new Set(roleIds.filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0))].sort(
        (a, b) => a - b
      ),
    [roleIds]
  );

  return useQuery({
    queryKey: catalogKey, queryFn: getCatalog,
    select: (catalog) => Object.fromEntries(validIds.map((roleId) => [roleId,
      catalog.skillRoles.filter((association) => association.roleIds.includes(roleId)).map((association) => association.skillId),
    ])),
    enabled: validIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
